import { ActionIcon, Button, Input, Modal, Title, rem } from "@mantine/core";
import IconX from "@src/assets/icons/tabler/x.svg?react";
import { useCreateFolder } from "@src/components/board/mutations/create-folder";
import { useMoveRanklistItem } from "@src/components/board/mutations/move-item";
import {
	type NavbarBranchItem,
	navbarBranchQueryKeys,
	useFlatNavbarTree,
} from "@src/components/board/queries/fetch-ranklist";
import { NavBarItemType, NavbarSection } from "@src/gql/graphql";
import {
	showErrorNotification,
	showSuccessNotification,
} from "@src/utils/notifications";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useCallback, useMemo, useState } from "react";
import { getTreeIndentPx } from "./tree-indent";

interface MoveToFolderModalProps {
	opened: boolean;
	onClose: () => void;
	itemKey: string;
	sourceParentKey: string | null;
	/** Branch list section (My Boards vs Shared With Me); defaults to My Boards. */
	section?: NavbarSection;
}

function FolderIcon({ size = 14 }: { size?: number }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="shrink-0"
		>
			<path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
		</svg>
	);
}

export function MoveToFolderModal({
	opened,
	onClose,
	itemKey,
	sourceParentKey,
	section = NavbarSection.MyWhiteboards,
}: MoveToFolderModalProps) {
	const queryClient = useQueryClient();
	const allRows = useFlatNavbarTree(section);
	const { mutateAsync: moveItem, isPending: isMoving } = useMoveRanklistItem();
	const { mutateAsync: createFolder, isPending: isCreating } =
		useCreateFolder();

	const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
	const [isCreatingNew, setIsCreatingNew] = useState(false);
	const [newFolderName, setNewFolderName] = useState("");

	const folders = useMemo(
		() => allRows.filter((row) => row.type === NavBarItemType.Folder),
		[allRows],
	);

	const handleCreateFolder = useCallback(async () => {
		const trimmed = newFolderName.trim();
		if (!trimmed) return;

		try {
			const result = await createFolder({
				section,
				name: trimmed,
				parentFolderKey: null,
			});
			if (result?.key) {
				setSelectedFolderId(result.key);
			}
			setIsCreatingNew(false);
			setNewFolderName("");
		} catch {
			showErrorNotification("Error", "Failed to create folder");
		}
	}, [newFolderName, createFolder, section]);

	const handleMove = useCallback(async () => {
		if (!selectedFolderId) return;

		try {
			const targetItems =
				queryClient.getQueryData<NavbarBranchItem[]>(
					navbarBranchQueryKeys.branch(section, selectedFolderId),
				) ?? [];

			await moveItem({
				section,
				itemKey,
				parentKey: selectedFolderId,
				sourceParentKey,
				afterKey: targetItems[targetItems.length - 1]?.key ?? null,
			});

			const folder = folders.find((f) => f.key === selectedFolderId);
			showSuccessNotification(
				"Successfully moved",
				`The board has been moved to "${folder?.name ?? "folder"}"`,
			);
			onClose();
		} catch {
			showErrorNotification(
				"Move failed",
				"The board could not be moved to the selected folder. Please try again later.",
			);
		}
	}, [
		selectedFolderId,
		itemKey,
		sourceParentKey,
		moveItem,
		section,
		queryClient,
		folders,
		onClose,
	]);

	const handleClose = () => {
		setSelectedFolderId(null);
		setIsCreatingNew(false);
		setNewFolderName("");
		onClose();
	};

	return (
		<Modal
			opened={opened}
			onClose={handleClose}
			radius="md"
			padding={rem(24)}
			withCloseButton={false}
			centered
			zIndex={2000}
		>
			<div className="mb-4 flex items-center justify-between">
				<Title order={4}>Move board to</Title>
				<ActionIcon
					variant="subtle"
					color="var(--ui-color-text-primary)"
					size="sm"
					onClick={handleClose}
					aria-label="Close move to folder modal"
					title="Close"
				>
					<IconX className="h-4 w-4" />
				</ActionIcon>
			</div>

			<div className="flex flex-col gap-0.5 max-h-60 overflow-y-auto">
				{folders.map((folder) => (
					<button
						key={folder.key}
						type="button"
						onClick={() => setSelectedFolderId(folder.key)}
						style={{ paddingLeft: getTreeIndentPx(folder.depth) }}
						className={clsx(
							"flex items-center gap-2 pr-3 py-2 rounded-md text-sm text-left w-full",
							"transition-colors cursor-pointer",
							selectedFolderId === folder.key
								? "bg-interaction-selected text-brand-primary"
								: "text-text-primary hover:bg-surface-hover-subtle",
						)}
					>
						<FolderIcon />
						<span className="truncate">{folder.name}</span>
					</button>
				))}

				{isCreatingNew && (
					<div className="flex items-center gap-2 px-3 py-1">
						<FolderIcon />
						<Input
							value={newFolderName}
							placeholder="New Folder"
							size="xs"
							radius="md"
							classNames={{ input: "focus:border-brand-primary" }}
							autoFocus
							className="flex-1"
							onChange={(e) => setNewFolderName(e.currentTarget.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleCreateFolder();
								if (e.key === "Escape") {
									setIsCreatingNew(false);
									setNewFolderName("");
								}
							}}
							onBlur={() => {
								if (!newFolderName.trim()) {
									setIsCreatingNew(false);
								}
							}}
						/>
					</div>
				)}
			</div>

			<div className="flex items-center justify-between mt-4">
				<Button
					variant="subtle"
					size="xs"
					color="var(--ui-color-text-primary)"
					onClick={() => setIsCreatingNew(true)}
					disabled={isCreatingNew}
				>
					Create Folder
				</Button>
				<div className="flex gap-2">
					<Button
						variant="subtle"
						size="xs"
						color="var(--ui-color-text-primary)"
						onClick={handleClose}
					>
						Cancel
					</Button>
					<Button
						size="xs"
						className="bg-brand-primary text-text-inverse hover:bg-brand-primary-hover"
						onClick={handleMove}
						disabled={!selectedFolderId}
						loading={isMoving || isCreating}
					>
						Move
					</Button>
				</div>
			</div>
		</Modal>
	);
}
